import React from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Icon,
  type Ryecon,
  RyeconCamera,
  RyeconChartColumn,
  RyeconClipboardSuccess,
  RyeconCrosshair,
  RyeconDatabase,
  RyeconFile,
  RyeconFunction,
  RyeconGraphNodes,
  RyeconGroup,
  RyeconHelp,
  RyeconMeter,
  RyeconModel,
  RyeconProjects,
  RyeconSave,
  RyeconSeed,
  RyeconTableColumn,
  SizeType,
} from '@dbt-labs/sourdough';

/**
 * List of svgs in sinter/static/img/icons/.
 * Should map to all support dbt resource types + 'unknown' fallback.
 *
 * TODO: This should be moved under a typings folder but that will require a lot of changes.
 */
export const resourceTypes = [
  'analysis',
  'exposure',
  'macro',
  'metric',
  'model',
  'seed',
  'snapshot',
  'source',
  'test',
  'unit_test',
  'semantic_model',
  'group',
  'saved_query',
  'function',
] as const;
export const resourceTypesExplorer = [...resourceTypes, 'project', 'column'] as const;
export const resourceTypesWithColumns: readonly ResourceType[] = [
  'model',
  'source',
  'seed',
  'snapshot',
] as const;

export type ResourceType = (typeof resourceTypes)[number];
export type ResourceTypeOrUnknown = (typeof resourceTypes)[number] | 'unknown';
export type ResourceTypeExplorer = (typeof resourceTypesExplorer)[number];

export type SvgClassName = React.SVGAttributes<SVGSVGElement>['className'];

export type SvgParams = {
  className?: SvgClassName;
};

export type IconProps = {
  resource?: ResourceTypeExplorer | 'unknown';
  size?: SizeType;
  testId?: string;
} & React.ComponentPropsWithoutRef<'span'>;

export function getResourceType<
  TDefaultValue extends undefined | ResourceTypeExplorer | 'unknown',
>(
  resourceName: string | undefined,
  defaultValue?: TDefaultValue,
): ResourceTypeExplorer | TDefaultValue {
  if (resourceName === 'project' || resourceName === 'column') return resourceName;
  const index = resourceTypes.findIndex((item) => item === resourceName);
  return resourceTypes[index] || defaultValue;
}

export const resourceIconMap: Record<ResourceTypeExplorer | 'unknown', Ryecon> = {
  analysis: RyeconCrosshair,
  column: RyeconTableColumn,
  exposure: RyeconMeter,
  group: RyeconGroup,
  macro: RyeconFile,
  metric: RyeconChartColumn,
  model: RyeconModel,
  project: RyeconProjects,
  saved_query: RyeconSave,
  seed: RyeconSeed,
  semantic_model: RyeconGraphNodes,
  snapshot: RyeconCamera,
  source: RyeconDatabase,
  test: RyeconClipboardSuccess,
  unit_test: RyeconClipboardSuccess,
  function: RyeconFunction,
  unknown: RyeconHelp,
};

export const resourceNameMap: Record<ResourceTypeExplorer | 'unknown', string> = {
  analysis: 'Analysis',
  column: 'Column',
  exposure: 'Exposure',
  group: 'Group',
  macro: 'Macro',
  metric: 'Metric',
  model: 'Model',
  project: 'Project',
  saved_query: 'Saved Query',
  seed: 'Seed',
  semantic_model: 'Semantic Model',
  snapshot: 'Snapshot',
  source: 'Source',
  test: 'Test',
  unit_test: 'Unit Test',
  function: 'Function',
  unknown: 'Unknown',
};

export const DbtResourceIcon: React.FC<IconProps> = ({
  resource,
  size,
  className: classNameOverrides,
  testId,
}) => {
  const iconName = getResourceType(resource);
  if (iconName === undefined) return null;
  const className = twMerge('text-fgBlack text-center', classNameOverrides);
  const title = resourceNameMap[iconName];

  return (
    <Icon
      className={className}
      ryecon={resourceIconMap[iconName]}
      alt={title}
      size={size}
      testId={testId}
    />
  );
};
